import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { moodApi } from '../../api/moodApi'
import type {
  CreateMoodEntryRequest,
  MoodEntryDto,
  UpdateMoodEntryRequest,
} from '../../Models/Mood'

export type MoodFetchStatus = 'idle' | 'loading' | 'succeeded' | 'failed'

export type MoodState = {
  /** yyyy-MM-dd → entries for that calendar day (from API). */
  byDate: Record<string, MoodEntryDto[]>
  statusByDate: Record<string, MoodFetchStatus>
  /** All entries for the signed-in user (from GetMoodEntries), newest first. */
  allEntries: MoodEntryDto[]
  allStatus: MoodFetchStatus
  error: string | null
}

const sortByLoggedAtDesc = (entries: MoodEntryDto[]): MoodEntryDto[] =>
  [...entries].sort(
    (a, b) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime()
  )

const initialState: MoodState = {
  byDate: {},
  statusByDate: {},
  allEntries: [],
  allStatus: 'idle',
  error: null,
}

export const fetchAllMoodEntries = createAsyncThunk(
  'mood/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const entries = await moodApi.getAll()
      return sortByLoggedAtDesc(entries)
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as Error).message === 'string'
          ? (e as Error).message
          : 'Could not load mood history.'
      return rejectWithValue(msg)
    }
  }
)

export const fetchMoodEntriesByDate = createAsyncThunk(
  'mood/fetchByDate',
  async (dateKey: string, { rejectWithValue }) => {
    try {
      return { dateKey, entries: await moodApi.getByDate(dateKey) }
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as Error).message === 'string'
          ? (e as Error).message
          : 'Could not load mood entries.'
      return rejectWithValue(msg)
    }
  }
)

export const createMoodEntry = createAsyncThunk(
  'mood/create',
  async (
    { dateKey, body }: { dateKey: string; body: CreateMoodEntryRequest },
    { rejectWithValue }
  ) => {
    try {
      const created = await moodApi.create(body)
      return { dateKey, created }
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as Error).message === 'string'
          ? (e as Error).message
          : 'Could not save mood.'
      return rejectWithValue(msg)
    }
  }
)

export const updateMoodEntry = createAsyncThunk(
  'mood/update',
  async (
    {
      id,
      dateKey,
      patch,
    }: { id: string; dateKey: string; patch: UpdateMoodEntryRequest },
    { rejectWithValue }
  ) => {
    try {
      const updated = await moodApi.update(id, patch)
      return { dateKey, updated }
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as Error).message === 'string'
          ? (e as Error).message
          : 'Could not update mood.'
      return rejectWithValue(msg)
    }
  }
)

export const deleteMoodEntry = createAsyncThunk(
  'mood/delete',
  async ({ id, dateKey }: { id: string; dateKey: string }, { rejectWithValue }) => {
    try {
      await moodApi.delete(id)
      return { id, dateKey }
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as Error).message === 'string'
          ? (e as Error).message
          : 'Could not delete mood entry.'
      return rejectWithValue(msg)
    }
  }
)

const moodSlice = createSlice({
  name: 'mood',
  initialState,
  reducers: {
    clearMoodError(state) {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllMoodEntries.pending, (state) => {
        state.allStatus = 'loading'
        state.error = null
      })
      .addCase(fetchAllMoodEntries.fulfilled, (state, action) => {
        state.allStatus = 'succeeded'
        state.allEntries = action.payload
      })
      .addCase(fetchAllMoodEntries.rejected, (state, action) => {
        state.allStatus = 'failed'
        state.error =
          typeof action.payload === 'string' ? action.payload : 'Could not load mood history.'
      })
      .addCase(fetchMoodEntriesByDate.pending, (state, action) => {
        const dateKey = action.meta.arg
        state.statusByDate[dateKey] = 'loading'
        state.error = null
      })
      .addCase(fetchMoodEntriesByDate.fulfilled, (state, action) => {
        const { dateKey, entries } = action.payload
        state.statusByDate[dateKey] = 'succeeded'
        state.byDate[dateKey] = entries
      })
      .addCase(fetchMoodEntriesByDate.rejected, (state, action) => {
        const dateKey = action.meta.arg
        state.statusByDate[dateKey] = 'failed'
        state.error =
          typeof action.payload === 'string' ? action.payload : 'Could not load mood entries.'
      })
      .addCase(createMoodEntry.pending, (state) => {
        state.error = null
      })
      .addCase(createMoodEntry.fulfilled, (state, action) => {
        const { dateKey, created } = action.payload
        const list = state.byDate[dateKey] ?? []
        state.byDate[dateKey] = [created, ...list]
        state.allEntries = sortByLoggedAtDesc([
          created,
          ...state.allEntries.filter((e) => e.id !== created.id),
        ])
      })
      .addCase(createMoodEntry.rejected, (state, action) => {
        state.error =
          typeof action.payload === 'string' ? action.payload : 'Could not save mood.'
      })
      .addCase(updateMoodEntry.fulfilled, (state, action) => {
        const { dateKey, updated } = action.payload
        const list = state.byDate[dateKey]
        if (list) {
          state.byDate[dateKey] = list.map((e) => (e.id === updated.id ? updated : e))
        }
        const ix = state.allEntries.findIndex((e) => e.id === updated.id)
        if (ix >= 0) state.allEntries[ix] = updated
        else state.allEntries.push(updated)
        state.allEntries = sortByLoggedAtDesc(state.allEntries)
      })
      .addCase(updateMoodEntry.rejected, (state, action) => {
        state.error =
          typeof action.payload === 'string' ? action.payload : 'Could not update mood.'
      })
      .addCase(deleteMoodEntry.fulfilled, (state, action) => {
        const { id, dateKey } = action.payload
        const list = state.byDate[dateKey]
        if (list) {
          state.byDate[dateKey] = list.filter((e) => e.id !== id)
        }
        state.allEntries = state.allEntries.filter((e) => e.id !== id)
      })
      .addCase(deleteMoodEntry.rejected, (state, action) => {
        state.error =
          typeof action.payload === 'string' ? action.payload : 'Could not delete mood entry.'
      })
  },
})

export const { clearMoodError } = moodSlice.actions
export default moodSlice.reducer

export const selectMoodEntriesForDate =
  (dateKey: string) =>
  (state: { mood: MoodState }): MoodEntryDto[] =>
    state.mood.byDate[dateKey] ?? []

/** True until the first fetch for this date finishes (success or failure). */
export const selectMoodLoadingForDate =
  (dateKey: string) =>
  (state: { mood: MoodState }): boolean => {
    const s = state.mood.statusByDate[dateKey]
    return s === undefined || s === 'loading'
  }

export const selectMoodError = (state: { mood: MoodState }) => state.mood.error

export const selectAllMoodEntries = (state: { mood: MoodState }) => state.mood.allEntries

export const selectAllMoodStatus = (state: { mood: MoodState }) => state.mood.allStatus
